import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/admin-api";
import { fmtDate, fmtRelative, useAdminList } from "./_useAdminList";

export default function AdminCompetitors() {
  const [q, setQ] = useState("");
  const [storeId, setStoreId] = useState("");
  const [status, setStatus] = useState("any");
  const [syncStatus, setSyncStatus] = useState("any");
  const [busyId, setBusyId] = useState(null);

  const list = useAdminList({
    fetcher: adminApi.competitors,
    dataKey: "competitors",
    defaultPageSize: 50,
  });

  const totalPages = Math.max(1, Math.ceil(list.total / list.pageSize));

  const applyFilters = () =>
    list.setFilters({
      q,
      storeId: storeId ? parseInt(storeId, 10) : undefined,
      status: status === "any" ? undefined : status,
      syncStatus: syncStatus === "any" ? undefined : syncStatus,
    });

  const triggerSync = async (id) => {
    setBusyId(id);
    const t = toast.loading(`Refreshing competitor ${id}…`);
    try {
      const data = await adminApi.ops.syncCompetitor(id);
      toast.success(data?.message || "Refresh queued.", { id: t });
      list.refetch();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.response?.data?.error || err.message,
        { id: t },
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Competitors</h1>
          <p className="text-sm text-muted-foreground">
            Cross-store view. {list.total.toLocaleString()} matching the current filter.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${list.refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <div className="relative sm:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
            placeholder="Search name / brand / domain"
            className="pl-9 h-9"
          />
        </div>
        <Input
          type="number"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          placeholder="Store ID"
          className="h-9"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            <SelectItem value="watched">watched</SelectItem>
            <SelectItem value="active">active</SelectItem>
            <SelectItem value="archived">archived</SelectItem>
            <SelectItem value="suspended">suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={syncStatus} onValueChange={setSyncStatus}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Sync" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any sync</SelectItem>
            <SelectItem value="idle">idle</SelectItem>
            <SelectItem value="syncing">syncing</SelectItem>
            <SelectItem value="complete">complete</SelectItem>
            <SelectItem value="failed">failed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={applyFilters}>Apply</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setQ("");
            setStoreId("");
            setStatus("any");
            setSyncStatus("any");
            list.setFilters({ q: "", storeId: undefined, status: undefined, syncStatus: undefined });
          }}
        >
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading competitors…
            </div>
          ) : list.error ? (
            <div className="p-6 text-sm text-destructive">{list.error}</div>
          ) : list.data.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No competitors match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Brand / Domain</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sync</TableHead>
                  <TableHead className="text-right">Ads</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs">{c.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.logoUrl ? (
                          <img
                            src={c.logoUrl}
                            alt=""
                            className="h-6 w-6 rounded object-contain bg-muted"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="h-6 w-6 rounded bg-muted text-[10px] flex items-center justify-center text-muted-foreground">
                            {(c.brandName || c.name || "?").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.brandName || c.name}</div>
                          {c.domain && (
                            <a
                              href={`https://${c.domain}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1"
                            >
                              {c.domain} <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.store ? (
                        <>
                          <span className="font-mono">{c.store.id}</span>
                          <div className="text-muted-foreground truncate max-w-[160px]">
                            {c.store.shopDomain}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{c.status || "—"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          c.syncStatus === "complete"
                            ? "default"
                            : c.syncStatus === "syncing"
                              ? "secondary"
                              : c.syncStatus === "failed"
                                ? "destructive"
                                : "outline"
                        }
                        className="text-xs"
                      >
                        {c.syncStatus || "idle"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div>{c._count?.ads ?? 0}</div>
                      {c.adCount != null && c.adCount !== c._count?.ads && (
                        <div className="text-[10px] text-muted-foreground">cached: {c.adCount}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">{fmtRelative(c.lastSyncAt)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtDate(c.lastSyncAt)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === c.id}
                        onClick={() => triggerSync(c.id)}
                      >
                        {busyId === c.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="ml-1.5">Sync</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Page {list.page} of {totalPages} · {list.total.toLocaleString()} competitors
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={list.page <= 1}
            onClick={() => list.setPage(list.page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={list.page >= totalPages}
            onClick={() => list.setPage(list.page + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
